import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import ee from '../../../services/earth-engine';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import GoogleMap from '../map/GoogleMap';
import ShapeEditControl from '../map/ShapeEditControl';
import { useShapeVertexEditing } from '../../../hooks/useShapeVertexEditing';
import StepperButtons from '../acquisition/StepperButtons';
import { NEXT } from '../../../hooks/useStepNavigations';
import * as MapGoogle from '../../../common/map';
import { aoiGeometry, aoiPoints, isMultiPolygon, parseKmlArea } from '../../../common/aoi';
import { Actions as Acquisition } from '../../../store/ducks/acquisition';
import { Actions as Snack } from '../../../store/ducks/snacks';
import TourGuider from '../tour/TourGuider';
import { useLocalStorage } from '../../../common/utils';
import HelpButton from '../core/HelpButton';
import { getSelectedSatellite } from '../../../algorithms/utils';
import { HelpOutlineOutlined, CloudUpload } from '@material-ui/icons';
import { calculateCloudCoverage as SentinelCloudCoverageCache } from '../../../algorithms/satellite/sentinel';
import { getIntertidalShapes } from '../../../algorithms/utils';
import AttentionMessage from '../core/AttentionMessage';
import { calculateCloudCoverage } from '../../../algorithms/satellite/landsat';
import { eeEval } from '../../../algorithms/utils/index.js';
import ActivityIndicator from '../core/ActivityIndicator.jsx';

/**
 * @function AOIChooser
 * @description Second step of the intertidal acquisition wizard. Lets the user define the Area
 * of Interest (AOI) — by drawing on the map or importing a KML —, the analysis period and cloud
 * filter, and the tide gauge data (CSV + timezone) required for the intertidal analysis.
 * @param {Object} props - Component properties.
 * @param {Function} props.navigate - Step navigation handler, forwarded to StepperButtons.
 * @see {@link handleDrawing} - stores the drawn/imported AOI.
 * @see {@link handleChoose} - validates and submits the step.
 * **Called by:
 * @see {IntertidalPage} - as the second acquisition step.
 * @memberof module:INTERTIDAL
 * @returns {JSX.Element} The AOI/period/tide-data chooser UI.
 */
const AOIChooser = ({ navigate, ...extra }) => {
  const dispatch = useDispatch();
  const availableDates = useSelector((state) => state.acquisition.availableDates);
  const mapRef = useRef();
  const mangroveLayer = useRef();
  const [t] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const aoiRef = useRef(null);

  /**
   * @description Lets the user drag the drawn AOI's existing points, persisting each adjustment to
   * local storage the same way a fresh draw does.
   * @see {@link useShapeVertexEditing}
   * @memberof module:INTERTIDAL
   */
  const aoiEditing = useShapeVertexEditing({
    onChange: (coords) => {
      setCoordinates(coords);
      window.localStorage.setItem('AOIIntertidal', JSON.stringify(coords));
    },
  });
  const [coordinates, setCoordinates] = useState([]);
  const [tidalVisibility, setTidalVisibility] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [kmlFileName, setKmlFileName] = useState('');
  const [tideFileName, setTideFileName] = useState('');
  const [periodStartDate, setPeriodStartDate] = useState('');
  const [periodEndDate, setPeriodEndDate] = useState('');
  const [cloudFilter, setCloudFilter] = useState(0.05);
  const [timezone, setTimezone] = useState('');
  const [referenceDialogOpen, setReferenceDialogOpen] = useState(false);
  const [showAttention, setShowAttention] = useState(false);
  const [tideData, setTideData] = useState([]);
  const [zoneOnly, setZoneOnly] = useState(false);

  useEffect(() => {
    const aoiFileName = window.localStorage.getItem('AOIIntertidalFileName');
    const tideFileName = window.localStorage.getItem('tideFileName');
    const period = window.localStorage.getItem('intertidalPeriodData');
    const tide = window.localStorage.getItem('tideData');
    const tz = window.localStorage.getItem('timezone');
    // Deliberately not restored from a previous visit like the other fields below: "zone only"
    // changes what's required/skipped for the whole analysis, so it always starts off and must be
    // turned on explicitly each time — also resets any stale 'true' left over from an earlier
    // session, so result.jsx's own direct localStorage read never disagrees with what's shown here.
    window.localStorage.setItem('intertidalZoneOnly', 'false');
    if (aoiFileName) setKmlFileName(aoiFileName);
    if (tideFileName) setTideFileName(tideFileName);
    if (period) {
      try {
        const saved = JSON.parse(period);
        if (saved.startDate) setPeriodStartDate(saved.startDate);
        if (saved.endDate) setPeriodEndDate(saved.endDate);
        if (saved.cloudFilter !== undefined && saved.cloudFilter !== null) setCloudFilter(Number(saved.cloudFilter));
      } catch (e) {
        console.warn('Invalid saved period data', e);
      }
    }
    if (tide) {
      try {
        const saved = JSON.parse(tide);
        setTideData(saved);
      } catch (e) {
        console.warn('Invalid saved tide data', e);
      }
    }
    if (tz !== null && tz !== undefined && tz !== '') setTimezone(parseInt(tz, 10));
  }, []);

  /**
   * @constant tideDateRange
   * @description Min/max calendar date (`'YYYY-MM-DD'`) found in the imported tide CSV, derived
   * from each row's raw `'YYYY-MM-DD HH:mm:ss'` date string — a plain slice is enough since that
   * format sorts lexicographically the same as chronologically, so no date parsing library is
   * needed just to find the extremes. `null` while no tide data has been imported yet.
   * @memberof module:INTERTIDAL
   */
  const tideDateRange = useMemo(() => {
    if (!tideData.length) return null;
    const dates = tideData
      .map((row) => String(row[0]).slice(0, 10))
      .filter(Boolean)
      .sort();
    return dates.length ? { min: dates[0], max: dates[dates.length - 1] } : null;
  }, [tideData]);

  /**
   * @constant isTideDataCompatibleWithPeriod
   * @description Whether the imported tide data's date range overlaps the selected analysis
   * period. `true` (no warning) whenever either side of the comparison isn't fully known yet, so
   * the message only appears once the user has actually provided both a period and a tide file.
   * @memberof module:INTERTIDAL
   */
  const isTideDataCompatibleWithPeriod = useMemo(() => {
    if (!tideDateRange || !periodStartDate || !periodEndDate) return true;
    return periodStartDate <= tideDateRange.max && periodEndDate >= tideDateRange.min;
  }, [tideDateRange, periodStartDate, periodEndDate]);

  /**
   * @function saveZoomChange
   * @description Persists the map's current zoom level to session storage.
   * **Called by:
   * @see {AOIChooser} - polled every 5 seconds via setInterval.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const saveZoomChange = function () {
    let zoom = MapGoogle.getZoomLevel();
    if (typeof zoom !== 'undefined' && zoom !== 'undefined') window.sessionStorage.setItem('zoom_level', zoom);
  };

  useEffect(() => {
    window.sessionStorage.removeItem('zoom_level');
    MapGoogle.setDrawingControlsVisible(true);
    const intervalId = setInterval(saveZoomChange, 5000);
    return () => {
      clearInterval(intervalId);
      MapGoogle.onZoomChange(() => saveZoomChange());
    };
  }, []);

  /**
   * @function handleDrawing
   * @description Stores the drawn/imported AOI overlay and coordinates, persisting the
   * coordinates to local storage, disabling the drawing controls and fitting the map bounds.
   * @param {google.maps.Polygon|google.maps.Rectangle} overlay - Drawn AOI overlay.
   * @param {Array<Array<Number>>} coordinates - AOI coordinates as [lng, lat] pairs.
   * **Called by:
   * @see {AOIChooser} - via GoogleMap's onRegionDrawn prop.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleDrawing = (overlay, coordinates, type) => {
    setKmlFileName('');
    MapGoogle.setDrawingControlsVisible(false);
    if (aoiRef.current) aoiRef.current.setMap(null);
    aoiRef.current = overlay;
    aoiEditing.setShape(overlay, type);
    setCoordinates(coordinates);
    window.localStorage.setItem('AOIIntertidal', JSON.stringify(coordinates));
    window.localStorage.setItem('AOIIntertidalFileName', '');
    if (coordinates && coordinates.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      coordinates.forEach((c) => bounds.extend({ lat: c[1], lng: c[0] }));
      if (mapRef.current) mapRef.current.fitBounds(bounds, 0);
    }
  };

  /**
   * @function handleUndo
   * @description Clears the drawn AOI overlay and every persisted step (AOI, period, tide data,
   * timezone, zone-only choice) from storage, resetting the wizard's input fields and defined-step
   * flags.
   * **Called by:
   * @see {AOIChooser} - via the "Undo" button's onClick.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleUndo = () => {
    aoiEditing.setShape(null);
    if (aoiRef.current) aoiRef.current.setMap(null);
    setCoordinates(null);
    setKmlFileName('');
    setTideFileName('');
    setPeriodStartDate('');
    setPeriodEndDate('');
    setCloudFilter(0);
    setTimezone('');
    setTideData([]);
    setZoneOnly(false);
    window.localStorage.removeItem('AOIIntertidal');
    window.localStorage.removeItem('intertidalPeriodData');
    window.localStorage.removeItem('tideData');
    window.localStorage.removeItem('timezone');
    window.localStorage.removeItem('AOIIntertidalFileName');
    window.localStorage.removeItem('intertidalZoneOnly');
    MapGoogle.setDrawingControlsVisible(true);
  };

  /**
   * @function saveCloudCoverageValues
   * @description Calculates cloud coverage percentages for satellite images within a
   * specified region of interest (ROI) and date range. The results are cached in
   * session storage as a date-to-cloud-coverage mapping for quick retrieval.
   * @param {Object} satellite - Satellite object containing missions array with collection names
   * @param {String} startDate - date to start getting cloud coverage
   * @param {Object} roi - google map earth polygon
   * **Called by:
   * @see {handleChoose}
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const saveCloudCoverageValues = async function (satellite, startDate, roi) {
    const cloudPropertyName = 'roiCloudPercentage';
    const dtPropertyName = 'system:time_start';
    let collectionWithROICloud =
      satellite.name == 'Landsat'
        ? calculateCloudCoverage(satellite, roi, startDate, true)
        : SentinelCloudCoverageCache(roi);
    let imagesWithCloud = await eeEval(collectionWithROICloud);
    let dateCloudMap = imagesWithCloud.features.map(function (el) {
      return {
        dt: el.properties[dtPropertyName],
        cloud: el.properties[cloudPropertyName],
      };
    });
    window.sessionStorage.setItem('roiCloudCoveragePerDate', JSON.stringify(dateCloudMap));
  };

  /**
   * @function handleChoose
   * @description Validates and submits the AOI/period/tide-data step.
   * 1. Caches per-date cloud coverage for the ROI and selected satellite's start year.
   * 2. Dispatches the AOI to the acquisition state.
   * 3. Builds an Earth Engine image collection for the selected missions within the chosen
   *    period, filtered by cloud coverage threshold.
   * 4. Deduplicates same-day images, keeping the most cloud-relevant one (thumbnails aren't
   *    generated here — ImageListRefiner's own async saga fetches those on the next step).
   * 5. Dispatches the filtered/available dates and advances to the next step.
   * @see {@link saveCloudCoverageValues}
   * **Called by:
   * @see {AOIChooser} - via StepperButtons' onNext.
   * @memberof module:INTERTIDAL
   * @returns {Promise<void>}
   */
  const handleChoose = async () => {
    aoiEditing.stopEditing();
    const searchSignature = JSON.stringify({
      coordinates,
      period: window.localStorage.getItem('intertidalPeriodData'),
    });
    if (
      searchSignature === window.localStorage.getItem('intertidalLastSearchSignature') &&
      Array.isArray(availableDates) &&
      availableDates.length > 0
    ) {
      navigate(NEXT);
      return;
    }
    setIsProcessing(true);
    window.sessionStorage.removeItem('intertidalFullImageList');
    await new Promise((resolve) => setTimeout(resolve, 100));
    let satellite = getSelectedSatellite(true);
    const type = window.sessionStorage.getItem('typeSatellite');
    let missions = type === 'TOA' ? satellite.missions : satellite.surfaceReflectance;
    let startYear = missions[0].startYear;
    try {
      await saveCloudCoverageValues(satellite, `${startYear}-01-01`, aoiGeometry(coordinates));
      try {
        dispatch(Acquisition.setAOI(aoiRef.current, coordinates, aoiGeometry(coordinates), kmlFileName));
      } catch (e) {
        setIsProcessing(false);
        // Kept as a blocking window.alert (not the Snack notifier) on purpose: the redirect right
        // below navigates away immediately, and a non-blocking snackbar wouldn't be visible long
        // enough (or at all) before the page unloads.
        notifyError(t('forms.intertidal.geeAccessError'));
        window.location = 'https://earthengine.google.com/signup/';
        return;
      }
      try {
        if (missions && missions.length > 0) {
          window.sessionStorage.setItem('selectedMission', JSON.stringify(missions[0]));
        }
        let period = JSON.parse(window.localStorage.getItem('intertidalPeriodData'));
        const cloudCoverageThreshold = Number.parseFloat(period.cloudFilter);
        const geometry = aoiGeometry(coordinates);
        const eeDateStart = new ee.Date(period.startDate);
        const eeDateEnd = new ee.Date(period.endDate);
        let eeCollection = null;
        missions.forEach((mission, missionIndex) => {
          let col = ee
            .ImageCollection(mission.name)
            .filterBounds(geometry)
            .filter(ee.Filter.date(eeDateStart, eeDateEnd))
            .map((img) =>
              img.set({
                missionIndex: missionIndex,
                missionName: mission.name,
                missionShortname: mission.shortname,
              }),
            );

          eeCollection = eeCollection ? eeCollection.merge(col) : col;
        });
        let collection = await eeEval(eeCollection);
        let tempArray = [];
        let landsatCloudByDate = null;
        let sentinelCloudEntriesSorted = null;
        try {
          const dtCloudMapping = JSON.parse(window.sessionStorage.getItem('roiCloudCoveragePerDate'));
          if (dtCloudMapping) {
            landsatCloudByDate = new Map(dtCloudMapping.map((el) => [el.dt, el.cloud]));
            sentinelCloudEntriesSorted = [...dtCloudMapping].sort((a, b) => a.dt - b.dt);
          }
        } catch (cacheError) {
          console.log('Error reading cloud cache for Landsat:', cacheError);
        }
        /**
         * @function findNearestCloudEntry
         * @description Binary-searches `sortedEntries` (ascending by `dt`) for the entry closest
         * to `targetMillis` — mirrors maskS2Clouds' own "smallest |dt - target|" tie-break, just
         * without re-scanning the whole array for every call.
         * @param {Array<{dt: Number, cloud: Number}>} sortedEntries - Cache entries sorted by `dt`.
         * @param {Number} targetMillis - Target timestamp, in milliseconds.
         * **Called by:
         * @see {handleChoose} - once per Sentinel candidate image.
         * @memberof module:INTERTIDAL
         * @returns {{dt: Number, cloud: Number}|null} The closest entry, or null if none exist.
         */
        const findNearestCloudEntry = (sortedEntries, targetMillis) => {
          if (!sortedEntries || sortedEntries.length === 0) return null;
          let lo = 0;
          let hi = sortedEntries.length - 1;
          while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (sortedEntries[mid].dt < targetMillis) lo = mid + 1;
            else hi = mid;
          }
          if (
            lo > 0 &&
            Math.abs(sortedEntries[lo - 1].dt - targetMillis) < Math.abs(sortedEntries[lo].dt - targetMillis)
          ) {
            return sortedEntries[lo - 1];
          }
          return sortedEntries[lo];
        };
        collection.features.forEach((entry) => {
          let dt = new Date(entry.properties['system:time_start']);
          let cloudCoverage = 0;
          let missionIndex = entry.properties.missionIndex;
          let mission = missions[missionIndex];
          try {
            if (mission.shortname.startsWith('S2')) {
              const nearest = findNearestCloudEntry(sentinelCloudEntriesSorted, dt.getTime());
              cloudCoverage = nearest ? nearest.cloud : 0.01;
            } else if (
              mission.shortname.startsWith('LC') ||
              mission.shortname.startsWith('LT') ||
              mission.shortname.startsWith('LE')
            ) {
              const matchingCloud = landsatCloudByDate?.get(dt.getTime());
              cloudCoverage = matchingCloud !== undefined ? matchingCloud : -1;
            }
          } catch (e) {
            console.log('error cloud coverage', e);
          }
          tempArray.push({
            id: entry.id,
            name: mission.name,
            shortname: mission.shortname,
            date: dt,
            dateFormated: `${dt.getFullYear()}-${dt.getMonth() + 1}-${dt.getDate()}`,
            dateTime: dt.getTime(),
            content: cloudCoverage,
          });
        });
        const map = new Map();
        tempArray.forEach((imgObject) => {
          let objectKey = imgObject.dateFormated;
          let objectFound = map.get(objectKey);
          if (objectFound === undefined) {
            map.set(objectKey, imgObject);
            imgObject.imagesSameDay = [];
          } else {
            objectFound.imagesSameDay.push(imgObject);
          }
        });
        tempArray = Array.from(map, ([, value]) => ({ ...value }));
        tempArray = tempArray.map((imgObject) => {
          let biggerDate = imgObject.dateTime;
          imgObject.imagesSameDay.forEach((img) => {
            if (img.dateTime > biggerDate) {
              biggerDate = img.dateTime;
              imgObject.content = img.content;
            }
          });
          return imgObject;
        });
        console.log('Images with cloud coverage info:', tempArray);
        tempArray = tempArray.filter((img) => {
          return img.content === -1 || cloudCoverageThreshold >= img.content;
        });
        console.log('Filtered images based on cloud coverage threshold:', tempArray);
        dispatch(Acquisition.setAvailableDates(tempArray));
        window.localStorage.setItem('intertidalLastSearchSignature', searchSignature);
        setIsProcessing(false);
        navigate(NEXT);
      } catch (e) {
        console.error('Error image filter', e);
        setIsProcessing(false);
      }
    } catch (e) {
      console.error('Error in handleChoose', e);
      setIsProcessing(false);
    }
  };

  /**
   * @function handleTidal
   * @description Toggles the reference intertidal/mangrove shapes overlay on the map.
   * @param {boolean|Event} e - Either the desired visibility, or a checkbox change event.
   * **Called by:
   * @see {AOIChooser} - via the reference layer checkbox's onClick.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleTidalVisibility = (e) => {
    let visible = typeof e === 'boolean' ? e : e.target.checked;
    if (!mangroveLayer.current) mangroveLayer.current = getIntertidalShapes();
    visible ? mapRef.current.overlayMapTypes.push(mangroveLayer.current) : mapRef.current.overlayMapTypes.clear();
    setTidalVisibility(visible);
  };

  /**
   * @function readFileTideData
   * @description Reads the selected tide data CSV file, parses its (date, tide value) rows
   * (skipping the header), and persists them to local storage.
   * @param {Event} event - Input change event containing the selected CSV file.
   * **Called by:
   * @see {AOIChooser} - via the tide data file input's onChange.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const readFileTideData = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const MAX_SIZE_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_SIZE_BYTES) {
      dispatch(
        Snack.error(
          'intertidal-tide-file-too-large',
          t('forms.intertidal.tideFileTooLarge', { size: (file.size / 1024 / 1024).toFixed(2) }),
        ),
      );
      return;
    }
    setTideFileName(file.name);
    window.localStorage.setItem('tideFileName', file.name);
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      function () {
        const fileContent = reader.result;
        let lines = fileContent.split('\n');
        let tideDataFileContent = [];
        lines.splice(0, 1);
        lines.forEach((line) => {
          if (line !== '') {
            let columns = line.split(',');
            if (columns.length === 2) {
              tideDataFileContent.push([columns[0], columns[1].replace('\r', '')]);
            }
          }
        });
        window.localStorage.setItem('tideData', JSON.stringify(tideDataFileContent));
        setTideData(tideDataFileContent);
      },
      false,
    );
    if (file) reader.readAsText(file);
  };

  /**
   * @function readFileData
   * @description Reads the selected KML file, extracts its polygon coordinates, and sets it as
   * the current AOI on the map.
   * @param {Event} event - Input change event containing the selected KML file.
   * **Called by:
   * @see {AOIChooser} - via the KML file input's onChange.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const readFileData = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setKmlFileName(file.name);
    window.localStorage.setItem('AOIIntertidalFileName', file.name);
    setIsProcessing(true);
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      function () {
        try {
          console.log('File data:', reader.result);
          const fileContent = reader.result;
          let isKml = fileContent.indexOf('<coordinates>') !== -1;
          if (isKml) {
            const coordinatesArray = parseKmlArea(fileContent);
            if (coordinatesArray) {
              if (aoiRef.current) aoiRef.current.setMap(null);
              setCoordinates(null);
              if (!mapRef.current) {
                dispatch(Snack.error('intertidal-map-not-ready', t('forms.intertidal.mapnotready')));
                return;
              }
              const openRing = MapGoogle.toOpenRing(coordinatesArray);
              const overlayFixed = MapGoogle.drawOutline(openRing);
              overlayFixed.setMap(mapRef.current);
              aoiRef.current = overlayFixed;
              aoiEditing.setShape(isMultiPolygon(openRing) ? null : overlayFixed, 'polygon');
              setCoordinates(openRing);
              window.localStorage.setItem('AOIIntertidal', JSON.stringify(openRing));
              const bounds = new window.google.maps.LatLngBounds();
              aoiPoints(openRing).forEach(([lng, lat]) => bounds.extend({ lat, lng }));
              mapRef.current.fitBounds(bounds);
              MapGoogle.setDrawingControlsVisible(false);
            } else {
              dispatch(Snack.error('intertidal-kml-no-coordinates', t('forms.intertidal.kmlNoCoordinates')));
            }
          } else {
            dispatch(Snack.error('intertidal-kml-interpret-error', t('forms.intertidal.interpretFileError')));
          }
        } finally {
          setIsProcessing(false);
        }
      },
      false,
    );
    if (file) return reader.readAsText(file);
    setIsProcessing(false);
  };

  /**
   * @function handlePeriodFieldChange
   * @description Updates one of the period fields (start/end date or cloud filter) and, when
   * all fields are valid, persists the period to local storage and marks the period as defined.
   * @param {string} field - Which field changed: 'startDate', 'endDate' or 'cloudFilter'.
   * @param {string|number} value - The new field value.
   * **Called by:
   * @see {AOIChooser} - via the period card's inputs' onChange.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handlePeriodFieldChange = (field, value) => {
    const next = {
      startDate: periodStartDate,
      endDate: periodEndDate,
      cloudFilter,
      [field]: field === 'cloudFilter' ? Number(value) : value,
    };
    setPeriodStartDate(next.startDate);
    setPeriodEndDate(next.endDate);
    setCloudFilter(next.cloudFilter);
    const valid =
      next.startDate !== '' &&
      next.endDate !== '' &&
      next.cloudFilter !== '' &&
      new Date(next.startDate) <= new Date(next.endDate);
    if (valid) {
      window.localStorage.setItem('intertidalPeriodData', JSON.stringify(next));
    }
  };

  /**
   * @function handleTimezoneChange
   * @description Persists the selected timezone offset to local storage and marks it as selected.
   * @param {Object} e - Select change event.
   * **Called by:
   * @see {AOIChooser} - via the timezone Select's onChange.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleTimezoneChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (isNaN(value)) return;
    setTimezone(value);
    window.localStorage.setItem('timezone', value);
  };

  /**
   * @function handleZoneOnlyChange
   * @description Toggles "zone-only" mode: the user wants only Product 1 (the intertidal zone
   * delimitation from step 4) and not the tide-dependent topographic reconstruction (waterline
   * extraction + Kriging, on the result page) — which the zone delimitation doesn't need in the
   * first place. Persisted immediately so `result.jsx`'s mount effect can read it without a
   * round-trip through Redux.
   * @param {Object} e - Switch change event.
   * **Called by:
   * @see {AOIChooser} - via the zone-only Switch's onChange.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleZoneOnlyChange = (e) => {
    const checked = e.target.checked;
    setZoneOnly(checked);
    window.localStorage.setItem('intertidalZoneOnly', checked ? 'true' : 'false');
  };

  /**
   * @function handleMapLoad
   * @description Stores the map reference and, if an AOI was previously saved, redraws it and
   * fits the map bounds to it.
   * @param {google.maps.Map} map - Loaded Google Map instance.
   * **Called by:
   * @see {AOIChooser} - via GoogleMap's onLoad prop.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleMapLoad = (map) => {
    mapRef.current = map;
    const saved = window.localStorage.getItem('AOIIntertidal');
    if (!saved) return;
    const coords = MapGoogle.toOpenRing(JSON.parse(saved));
    const overlayObj = MapGoogle.drawOutline(coords);
    overlayObj.setMap(map);
    aoiRef.current = overlayObj;
    aoiEditing.setShape(isMultiPolygon(coords) ? null : overlayObj, 'polygon');
    setCoordinates(coords);
    const bounds = new window.google.maps.LatLngBounds();
    aoiPoints(coords).forEach(([lng, lat]) => bounds.extend({ lat, lng }));
    map.fitBounds(bounds);
    MapGoogle.setDrawingControlsVisible(false);
  };

  /**
   * @function toggleAttention
   * @description Toggles the visibility of the period-selection attention message.
   * **Called by:
   * @see {AOIChooser} - via the date-range help icon's onClick.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const toggleAttention = () => {
    setShowAttention((prev) => !prev);
  };

  /**
   * @function createItems
   * @description Builds the cloud filter threshold `<MenuItem>` elements.
   * **Called by:
   * @see {AOIChooser} - to populate the cloud filter select.
   * @memberof module:INTERTIDAL
   * @returns {Array<JSX.Element>} Menu items for the cloud filter select.
   */
  const createItems = () => {
    return [0, 0.025, 0.05, 0.1, 0.15, 0.2, 0.3, 0.4, 0.5, 1].map((value, i) => {
      const label = value === 0 ? `= ${value * 100} %` : `<= ${value * 100} %`;
      return (
        <MenuItem key={i} value={value}>
          {label}
        </MenuItem>
      );
    });
  };

  const steps = [
    {
      selector: '#areachooser',
      content: t('tour.interdial.2.aoi'),
    },
    {
      selector: '#interfialZone',
      content: t('tour.interdial.2.interdialZone'),
    },
    {
      selector: '#importKml',
      content: t('tour.interdial.2.importKml'),
    },
  ];

  const [isTourOpen, setIsTourOpen] = useLocalStorage('showROITour', true);

  return (
    <React.Fragment>
      {isProcessing && <ActivityIndicator forceShow={true} title={t('forms.core.loading.cloudCoverage')} />}
      <Box
        style={{
          opacity: isProcessing ? 0.5 : 1,
          pointerEvents: isProcessing ? 'none' : 'auto',
        }}>
        <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ padding: '0 24px' }}>
          <HelpButton
            onClickFunction={() => {
              setIsTourOpen(true);
            }}
          />
        </Box>
        <Paper
          elevation={1}
          style={{
            width: '100%',
            maxWidth: 1100,
            margin: '0 auto 24px auto',
            padding: '0 24px 24px 24px',
            borderRadius: 12,
          }}>
          <Box
            style={{
              width: '100%',
              maxWidth: 1100,
              margin: '0 0 12px 0',
              padding: '0 24px 12px 12px',
              boxSizing: 'border-box',
            }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
              {t('forms.acquisition.2.bathymetryAoiChooser.inputFilesAndSettings')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('forms.intertidal.2.description')}
            </Typography>
          </Box>
          <Paper
            variant="outlined"
            style={{
              padding: 16,
              borderRadius: 10,
              marginBottom: 12,
              backgroundColor: zoneOnly ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
            }}>
            <FormControlLabel
              control={<Switch checked={zoneOnly} onChange={handleZoneOnlyChange} color="primary" />}
              label={
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {t('forms.intertidal.zoneOnlyLabel')}
                </Typography>
              }
            />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              {t('forms.intertidal.zoneOnlyDescription')}
            </Typography>
          </Paper>
          <Paper variant="outlined" id="importKml" style={{ padding: 16, borderRadius: 10, marginBottom: 12 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 10 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {t('forms.acquisition.2.intertidalArea')}
              </Typography>
              <Typography color="error" style={{ fontWeight: 800, fontSize: 18 }}>
                *
              </Typography>
            </Box>
            <Button variant="outlined" color="primary" component="label" startIcon={<CloudUpload />}>
              {t('forms.acquisition.2.intertidalTideImportKmz')}
              <input
                hidden
                accept=".kml"
                type="file"
                onClick={(e) => {
                  e.target.value = null;
                }}
                onChange={readFileData}
              />
            </Button>
            <Typography variant="body2" style={{ marginTop: 10 }}>
              {kmlFileName ? (
                <>
                  {t('forms.acquisition.2.bathymetryAoiChooser.fileLabel')} <strong>{kmlFileName}</strong>
                </>
              ) : (
                t('forms.acquisition.2.noKmlFileSelected')
              )}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 6 }}>
              {coordinates
                ? t('forms.acquisition.2.bathymetryAoiChooser.aoiLoadedOnMap')
                : t('forms.acquisition.2.bathymetryAoiChooser.drawOnMapOrImportKml')}
            </Typography>
          </Paper>
          <Paper
            variant="outlined"
            style={{
              borderRadius: 12,
              overflow: 'hidden',
              marginBottom: 16,
              position: 'relative',
            }}>
            <div id="areachooser">
              <GoogleMap
                style={{ width: '100%', height: '56vh' }}
                onRegionDrawn={handleDrawing}
                onLoad={handleMapLoad}
              />
            </div>
            <ShapeEditControl editing={aoiEditing} visible={Boolean(coordinates?.length)} />
            <Box
              id="interfialZone"
              style={{
                position: 'absolute',
                top: 10,
                left: 10,
                backgroundColor: '#FFF',
                boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
                borderRadius: 2,
                border: '1px solid rgba(0, 0, 0, 0.3)',
                padding: '0 10px 0 0',
                display: 'flex',
                alignItems: 'center',
                zIndex: 99,
              }}>
              <Checkbox
                checked={tidalVisibility}
                onClick={() => handleTidalVisibility(!tidalVisibility)}
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="body2">{t('forms.acquisition.2.checkboxIntertidal')}</Typography>
              <Tooltip title={t('forms.acquisition.2.seeLayerReference')}>
                <IconButton
                  aria-label="reference"
                  size="small"
                  onClick={() => setReferenceDialogOpen(true)}
                  style={{ marginLeft: 4 }}>
                  <HelpOutlineOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
          <Box
            display="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16,
              marginBottom: 24,
            }}>
            <Paper variant="outlined" style={{ padding: 16, borderRadius: 10 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 10 }}>
                <Box display="flex" alignItems="center" gridGap={4}>
                  <IconButton aria-label="attention" size="small" onClick={toggleAttention} style={{ padding: 4 }}>
                    <HelpOutlineOutlined color="primary" fontSize="inherit" />
                  </IconButton>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    {t('forms.acquisition.2.intertidalPeriod')}
                  </Typography>
                </Box>
                <Typography color="error" style={{ fontWeight: 800, fontSize: 18 }}>
                  *
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" style={{ marginBottom: 10, gap: 12 }}>
                <TextField
                  type="date"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={periodStartDate}
                  onChange={(e) => handlePeriodFieldChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  label={t('forms.acquisition.2.intertidalPeriodStartDate')}
                  style={{ marginBottom: 12 }}
                />
                <TextField
                  type="date"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={periodEndDate}
                  onChange={(e) => handlePeriodFieldChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  label={t('forms.acquisition.2.intertidalPeriodEndDate')}
                  style={{ marginBottom: 12 }}
                />
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={cloudFilter}
                  onChange={(e) => handlePeriodFieldChange('cloudFilter', e.target.value)}
                  label={t('forms.acquisition.2.intertidalCloudFilter')}>
                  {createItems()}
                </TextField>
                {showAttention && (
                  <Box style={{ marginTop: 12 }}>
                    <AttentionMessage
                      title={t('forms.acquisition.4.attention')}
                      message={t('forms.intertidal.1.dateIntervalComments')}
                    />
                  </Box>
                )}
              </Box>
            </Paper>

            <Paper variant="outlined" style={{ padding: 16, borderRadius: 10 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 10 }}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {t('forms.acquisition.2.intertidalTideData')}
                </Typography>
                {zoneOnly ? (
                  <Typography variant="caption" color="textSecondary">
                    {t('forms.intertidal.optionalField')}
                  </Typography>
                ) : (
                  <Typography color="error" style={{ fontWeight: 800, fontSize: 18 }}>
                    *
                  </Typography>
                )}
              </Box>
              <Button variant="outlined" color="primary" component="label" startIcon={<CloudUpload />}>
                {t('forms.acquisition.2.bathymetryAoiChooser.importCsv')}
                <input
                  hidden
                  accept=".csv"
                  type="file"
                  onClick={(e) => {
                    e.target.value = null;
                  }}
                  onChange={readFileTideData}
                />
              </Button>
              <Typography variant="body2" style={{ marginTop: 10 }}>
                {tideFileName ? (
                  <>
                    {t('forms.acquisition.2.bathymetryAoiChooser.fileLabel')} <strong>{tideFileName}</strong>
                  </>
                ) : (
                  t('forms.acquisition.2.bathymetryAoiChooser.noCsvFileSelected')
                )}
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>
                {t('forms.acquisition.2.bathymetryAoiChooser.tideFileFormat')}
              </Typography>
              <Typography variant="caption" display="block" style={{ marginTop: 4 }}>
                <a href="/tide_Itaqui.csv" rel="noreferrer">
                  {t('forms.intertidal.downloadSampleTideData')}
                </a>
              </Typography>
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                value={timezone}
                onChange={handleTimezoneChange}
                label={t('forms.intertidal.timezone')}
                style={{ marginTop: 12 }}>
                <MenuItem value="">
                  <em>{t('forms.intertidal.selectValue')}</em>
                </MenuItem>
                {Array.from({ length: 25 }, (_, i) => i - 12).map((value) => (
                  <MenuItem key={value} value={value}>
                    {`GMT ${value >= 0 ? '+' : '-'}${String(Math.abs(value)).padStart(2, '0')}:00`}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                {t('forms.acquisition.2.bathymetryAoiChooser.timezoneHelper')}
              </Typography>
            </Paper>
          </Box>
          {!zoneOnly && !isTideDataCompatibleWithPeriod && (
            <Box style={{ marginTop: -8, marginBottom: 24 }}>
              <AttentionMessage
                title={t('forms.intertidal.tideDataPeriodMismatchTitle')}
                message={t('forms.intertidal.tideDataPeriodMismatchMessage', {
                  tideMin: tideDateRange.min,
                  tideMax: tideDateRange.max,
                  periodStart: periodStartDate,
                  periodEnd: periodEndDate,
                })}
              />
            </Box>
          )}
        </Paper>
        <StepperButtons
          navigate={(target) => {
            if (target !== NEXT) navigate(target);
          }}
          nextDisabled={
            aoiEditing.geometryError !== '' ||
            coordinates?.length === 0 ||
            !periodStartDate ||
            !periodEndDate ||
            isProcessing ||
            (!zoneOnly && (timezone === '' || tideData.length === 0 || !isTideDataCompatibleWithPeriod))
          }
          onNext={handleChoose}>
          <Button
            onClick={handleUndo}
            disabled={
              !!!coordinates?.length && timezone === '' && !periodStartDate && !periodEndDate && !!!tideData.length
            }
            color="secondary"
            variant="outlined">
            {t('forms.acquisition.2.undo')}
          </Button>
        </StepperButtons>
        <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
        <Dialog open={referenceDialogOpen} onClose={() => setReferenceDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('forms.acquisition.2.referencePossibleTideGauge')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" paragraph>
              {t('forms.acquisition.2.possibleTideGaugeButtonToltip')}
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              {t('forms.acquisition.2.references')}
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
              Murray, N.J., Phinn, S.R., DeWitt, M., Ferrari, R., Johnston, R., Lyons, M.B., Clinton, N., Thau, D. &
              Fuller, R.A. (2019) The global distribution and trajectory of tidal flats. Nature, 565, 222-225.
              doi:10.1038/s41586-018-0805-8
            </Typography>
            <Typography variant="caption" display="block">
              Murray, N. J., Phinn, S. P., Fuller, R. A., DeWitt, M., Ferrari, R., Johnston, R., Clinton, N., & Lyons,
              M. B. (2022). Mapas globais de alta resolução dos ecossistemas de planícies de maré de 1984 a 2019.
              Scientific Data, 9(1). doi:10.1038/s41597-022-01635-5
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" color="primary" onClick={() => setReferenceDialogOpen(false)}>
              {t('forms.acquisition.2.close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </React.Fragment>
  );
};

export default AOIChooser;
